<script>
    import { createForm } from "svelte-forms-lib";
    import { navigate } from "svelte-routing";
    import { onMount } from 'svelte';
    import {
        createGroup,
        getGroup,
        updateGroup,
        deleteGroup,
        getPossibleParents
    } from "../../../services/groupService";
    import GroupSearcher from "../../SearchBars/GroupSearcher.svelte";
    import InputField from "../InputField.svelte";
    import FormCard from "../FormCard.svelte";
    import * as yup from "yup";
    import { getOptionalSelectValue } from "../../../utils/getOptionalSelectValue";
    import { groupTypeByAncestorCount } from "../../../utils/subscribers";
    import Select from "svelte-select";
    import toast from "svelte-french-toast";
    import { isAdmin, isGroupManager, isRootManager } from "../../../stores/settingsStore";

    const urlParams = new URLSearchParams(window.location.search);
    const groupId = urlParams.get('id');
    let isEdit = !!groupId;
    let isPressed = false;
    let parentGroups = [];

    // A Circolo must have at least 12 subscribers: it is the default on creation
    // and the value pre-filled when a Federazione is picked as parent.
    const CIRCLE_MIN_SUBSCRIBERS = 12;

    // Only admins and national (root) managers may change the minimum
    $: canEditMinSubscribers = $isAdmin || $isRootManager;

    const { form, handleChange, handleSubmit, isValid, errors } = createForm({
        initialValues: {
            name: "",
            parentId: "",
            minSubscribers: isEdit ? "" : CIRCLE_MIN_SUBSCRIBERS,
            parent: undefined
        },
        validationSchema: yup.object().shape({
            name: yup.string().required("Inserisci un nome"),
            parentId: yup.mixed(),
            minSubscribers: yup.number()
             .typeError("Inserisci un numero corretto")
             .nullable()
             .moreThan(0, "Inserisci un numero maggiore o uguale a 1")
             .transform((_, val) => (!!val && !isNaN(+val) ? Number(val) : null)),
            parent: yup.object().optional()
        }),
        onSubmit: (formGroup) => {
            isPressed = true;

            formGroup.parentId = getOptionalSelectValue(formGroup.parentId);

            if (formGroup.parentId === "") {
                delete formGroup.parentId;
            }

            if (formGroup.minSubscribers === "" || !canEditMinSubscribers) {
                delete formGroup.minSubscribers;
            }

            return isEdit ? edit(formGroup) : add(formGroup);
        },
    });

    async function edit(group) {
        updateGroup(group, groupId).then((resp) => {
            isPressed = false;

            if(resp.status === 200 && resp?.data) {
                toast.success("Gruppo modificato");
            }
        }).catch((err) => {
            isPressed = false;

            if(err?.response?.status === 500) {
                toast.error(`Impossibile modificare il gruppo, errore ${err.toString()}`);
            }
        })
    }

    async function add(group) {
        createGroup(group).then((resp) => {
            isPressed = false;
            if(resp.status === 200 && resp?.data) {
                toast.success("Gruppo creato");
                navigate("table", {replace: true})
            }
        }).catch((err) => {
            isPressed = false;

            if(err?.response?.status === 500) {
                toast.error(`Impossibile creare il gruppo, errore ${err.toString()}`);
            }
        })
    }

    async function deleteById(groupId) {
        deleteGroup(groupId).then((resp) => {
            if(resp.status === 200 && resp?.data) {
                toast.success("Gruppo eliminato");
                navigate("table", {replace: true})
            }
        }).catch((err) => {
            isPressed = false;

            toast.error(`Impossibile eliminare il gruppo, errore ${err.toString()}`);
        })
    }

    function setFormValue(group) {
        parentGroups = !!group.parent ? [{ value: group.parent.id, label: group.parent.name }] : [];

        $form.minSubscribers = group.minSubscribers;
        $form.name = group.name;
        $form.parentId = group.parent.id ?? null;
    }

    // When the selected parent is a Federazione the new group is a Circolo:
    // restore the minimum if the field has been cleared.
    function handleParentSelect(event) {
        const parent = event?.detail;
        const parentType = parent?.ancestorsCount != null
            ? groupTypeByAncestorCount.find((g) => g.match(parent.ancestorsCount))?.value
            : undefined;

        const isMinSubscribersEmpty = $form.minSubscribers === "" || $form.minSubscribers == null;

        if (parentType === "Federazione" && isMinSubscribersEmpty) {
            $form.minSubscribers = CIRCLE_MIN_SUBSCRIBERS;
        }
    }

    async function getPossibleGroupParents(searchText) {
        return await getPossibleParents(groupId, searchText)
            .then(res => [
                ...res.data.map((g) => ({ label: g.name, value: g.id })),
                ...($form.parent ? [ { label: $form.parent.name, value: $form.parent.id  } ] : [])
            ]);
    }

    onMount(async () => {
        if (!isEdit) { return }

        getGroup(groupId).then((data) => {
            if (data?.status === 200) {
                setFormValue(data.data);
            }
        }).catch((err) => {
            if(err?.response?.data?.status === 404 || err?.response?.data?.status === 500) {
                toast.error("Gruppo non trovato")
                navigate("table" , {replace: false})
                return;
            }
            defaultError();
        });
    });
</script>

<FormCard
    subject="il gruppo"
    isEdit={isEdit}
    isButtonDisabled={isPressed || !$isValid}
    hasDeleteButton={!$isGroupManager}
    handleSubmit={handleSubmit}
    handleDelete={() => deleteById(groupId)}
>
    <InputField
        fieldName="Nome del gruppo"
        placeholder="Nome del gruppo"
        isRequired={false}
        name="name"
        id="name"
        bind:errorText={$errors.name}
        bind:value={$form.name}
        handleChange={handleChange}
    />

    <div class="w-full">
        <div class="font-450 text-lg">Gruppo Padre</div>

        { #if isEdit }
            <Select
                id="groupId"
                items={parentGroups}
                loadOptions={getPossibleGroupParents}
                maxlength="200"
                type="text"
                placeholder="Nome del gruppo"
                debounceWait={300}
                bind:value={$form.parentId}
                on:change={handleChange}
                class="rounded"
                required={true}
                containerStyles={`
                    border: 1px solid #e0e0e0;
                    border-radius: 0px;
                    box-shadow: 0px 1px 4px 0px rgb(0 0 0 / 15%);
                    height: 2.75rem
                `}
                >
                <div slot="empty" class="flex justify-center py-3">Digita il nome di un gruppo</div>
            </Select>
        {:else}
            <GroupSearcher
                bind:value={$form.parentId}
                on:change={handleParentSelect}
                isUserBased="{true}"
                required={false}
            />
        {/if}
    </div>

    <InputField
        fieldName="Numero minimo di iscritti"
        placeholder="Numero minimo di iscritti a questo gruppo"
        id="form-minSubscribers"
        type="number"
        isRequired={false}
        name="minSubscribers"
        handleChange={handleChange}
        isDisabled={!canEditMinSubscribers}
        bind:errorText={$errors.minSubscribers}
        bind:value={$form.minSubscribers}
    />
</FormCard>